import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import { Headphones, Mail, Lock, User as UserIcon, Loader2, Check, Radio, Library, Sparkles, Heart } from 'lucide-react';

const PERKS = [
  { icon: Headphones, title: '24/7 ReaperCast Radio', body: 'Discover indie music from working artists — always live.' },
  { icon: Library,    title: 'Knowledge Hub',          body: 'Ebooks, audiobooks, workbooks, podcasts from real musicians.' },
  { icon: Sparkles,   title: 'Free forever tier',      body: 'Listen, read, learn at zero cost. Upgrade only if you want extras.' },
  { icon: Heart,      title: 'Follow your favorites',  body: 'Subscribe to artists, save music, support what you love.' },
];

export default function ListenerSignup() {
  const nav = useNavigate();
  const { signup } = useApp();
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !pwd) { toast.error('Email and password required'); return; }
    setLoading(true);
    try {
      await signup(email, pwd, name || undefined, { profile_type: 'listener' });
      toast.success('Welcome — start exploring.');
      nav('/feed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="listener-signup" className="min-h-screen bg-[#0a0a0c] text-white flex">
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,10,12,0.92), rgba(10,10,12,0.35) 55%, rgba(34,211,238,0.28))' }} />
        <div className="relative p-12 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2"><Logo /><span className="chip active text-[10px] uppercase">Listener</span></Link>
          <div>
            <div className="flex items-center gap-2 text-[#22d3ee] mb-3"><Radio className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">For music fans</span></div>
            <h2 className="font-display text-[56px] leading-[0.98] max-w-[520px]">Listen. Learn. <span className="brand-gradient">Support.</span></h2>
            <p className="text-white/80 text-[15px] mt-4 max-w-[480px]">You're here for the music, not to run a business. Tune in to live radio, save tracks, follow artists, and dig into Knowledge Hub content.</p>
            <ul className="mt-8 space-y-3 max-w-[480px]">
              {PERKS.map(p => (
                <li key={p.title} className="flex items-start gap-3 text-[13.5px]">
                  <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: 'rgba(34,211,238,0.18)', color: '#22d3ee' }}><p.icon className="w-4 h-4" /></div>
                  <div>
                    <div className="font-semibold">{p.title}</div>
                    <div className="text-white/65">{p.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[520px] flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden mb-6 flex items-center gap-2"><Link to="/"><Logo /></Link><span className="chip active text-[10px] uppercase">Listener</span></div>
          <div className="flex items-center gap-2 text-[#22d3ee] mb-2"><Headphones className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Listener signup</span></div>
          <h1 className="font-display text-[32px] leading-none">Jump in.</h1>
          <p className="text-white/60 text-[14px] mt-2">Free forever. No artist tools, no clutter — just listening.</p>

          <form onSubmit={submit} className="mt-6 space-y-3">
            <label className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-lg px-3 focus-within:border-[#22d3ee]/60">
              <UserIcon className="w-4 h-4 text-white/50" />
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Display name" className="bg-transparent flex-1 py-3 focus:outline-none text-[14px] placeholder:text-white/40" data-testid="listener-name" />
            </label>
            <label className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-lg px-3 focus-within:border-[#22d3ee]/60">
              <Mail className="w-4 h-4 text-white/50" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@domain.com" className="bg-transparent flex-1 py-3 focus:outline-none text-[14px] placeholder:text-white/40" data-testid="listener-email" required />
            </label>
            <label className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-lg px-3 focus-within:border-[#22d3ee]/60">
              <Lock className="w-4 h-4 text-white/50" />
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="••••••••" className="bg-transparent flex-1 py-3 focus:outline-none text-[14px] placeholder:text-white/40" data-testid="listener-password" required />
            </label>
            <button type="submit" disabled={loading} className="w-full py-3 rounded-full font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2" style={{ background: 'linear-gradient(135deg,#22d3ee,#a78bfa)' }} data-testid="listener-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Start listening
            </button>
          </form>

          <div className="text-[13px] text-white/60 mt-6 text-center">
            Already have an account? <Link to="/login" className="text-[#22d3ee] hover:underline">Sign in</Link>
            <div className="mt-1">Artist? <Link to="/reapercast/signup" className="text-[#a78bfa] hover:underline">Musician signup →</Link></div>
          </div>
        </div>
      </div>
    </div>
  );
}
